import { useEffect } from 'react';
import { COMMAND_PRIORITY_EDITOR, createCommand, $insertNodes, type LexicalCommand } from 'lexical';
import { useLexicalComposerContext } from '@lexical/react/LexicalComposerContext';
import { $createExponentNode, $createFractionNode, $createRootNode } from '../components/MathNodes';

export const INSERT_FRACTION_COMMAND: LexicalCommand<void> = createCommand('INSERT_FRACTION');
export const INSERT_EXPONENT_COMMAND: LexicalCommand<void> = createCommand('INSERT_EXPONENT');
export const INSERT_ROOT_COMMAND: LexicalCommand<{ indexed?: boolean }> =
    createCommand('INSERT_ROOT');

export function MathPlugin() {
    const [editor] = useLexicalComposerContext();

    function focusFirstSlotAfterInsert(editor: any, nodeKey: string) {
        requestAnimationFrame(() => {
            const dom = editor.getElementByKey(nodeKey) as HTMLElement | null;
            if (!dom) {
                return;
            }
            // Prefer numerator/base/radicand over index, etc.
            const firstEditable =
                dom.querySelector<HTMLElement>(
                    `.pm-math-num [contenteditable="true"],
                    .pm-math-base [contenteditable="true"],
                    .pm-math-root [contenteditable="true"],
                    .pm-math-radicand [contenteditable="true"],
                    .pm-math-overline [contenteditable="true"]`
                ) ?? dom.querySelector<HTMLElement>('[contenteditable="true"]');
            firstEditable?.focus();
        });
    }

    useEffect(() => {
        return editor.registerCommand(
            INSERT_FRACTION_COMMAND,
            () => {
                editor.update(() => {
                    const node = $createFractionNode();
                    $insertNodes([node]);
                    const key = node.getKey();
                    focusFirstSlotAfterInsert(editor, key);
                });
                return true;
            },
            COMMAND_PRIORITY_EDITOR
        );
    }, [editor]);

    useEffect(() => {
        return editor.registerCommand(
            INSERT_EXPONENT_COMMAND,
            () => {
                editor.update(() => {
                    const node = $createExponentNode();
                    $insertNodes([node]);
                    const key = node.getKey();
                    focusFirstSlotAfterInsert(editor, key);
                });
                return true;
            },
            COMMAND_PRIORITY_EDITOR
        );
    }, [editor]);

    useEffect(() => {
        return editor.registerCommand(
            INSERT_ROOT_COMMAND,
            (payload) => {
                editor.update(() => {
                    const node = $createRootNode(Boolean(payload?.indexed));
                    $insertNodes([node]);
                    const key = node.getKey();
                    focusFirstSlotAfterInsert(editor, key);
                });
                return true;
            },
            COMMAND_PRIORITY_EDITOR
        );
    }, [editor]);

    return null;
}
